// src/main.jsx  (ANDROID SAFE — NO AUTO LOGOUT / NO STRICTMODE DOUBLE AUTH / COPY-PASTE READY)

import React, { useEffect, useMemo, useRef, useState, createContext } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import { AuthProvider } from "./context/AuthContext";

const IS_CAPACITOR =
  window.location.protocol === "capacitor:" ||
  window.location.protocol === "file:" ||
  !!window.Capacitor;

const DEFAULT_API_BASE = "https://api.bamboome.me";

export const RuntimeContext = createContext({
  apiBase: DEFAULT_API_BASE,
  connection: null,
  themePref: "system",
  online: true,
  setApiBase: () => {},
  setThemePref: () => {},
});

const LS_API = "API_BASE";
const LS_THEME = "THEME_PREF";
const SW_URL = "./sw.js";

function readApiBase() {
  return (
    window.__API_BASE__ ||
    (typeof localStorage !== "undefined" && localStorage.getItem(LS_API)) ||
    DEFAULT_API_BASE
  );
}

function readThemePref() {
  return (
    (typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme")) ||
    (typeof localStorage !== "undefined" && localStorage.getItem(LS_THEME)) ||
    "system"
  );
}

function readConnection() {
  return (
    window.__CONNECTION__ ||
    (navigator.connection
      ? {
          downlink: navigator.connection.downlink,
          effectiveType: navigator.connection.effectiveType,
          saveData: navigator.connection.saveData,
          rtt: navigator.connection.rtt,
        }
      : null)
  );
}

function isSecureForSW() {
  if (typeof window === "undefined") return false;
  if (IS_CAPACITOR) return false;

  const { protocol, hostname } = window.location;
  if (protocol === "https:") return true;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  return false;
}

const AUTH_STYLE = (import.meta?.env?.VITE_AUTH_STYLE || "bearer")
  .toLowerCase()
  .trim();

const BRANCH_HEADER = (import.meta?.env?.VITE_BRANCH_HEADER || "").trim();
const BRANCH_VALUE = (import.meta?.env?.VITE_BRANCH_VALUE || "").trim();

function buildAuthHeaders() {
  let token = "";
  try {
    token = localStorage.getItem("auth_token") || "";
  } catch {}

  const h = {};
  if (token) {
    if (AUTH_STYLE === "x-token") h["x-token"] = token;
    else h["Authorization"] = `Bearer ${token}`;
  }
  if (BRANCH_HEADER && BRANCH_VALUE) {
    h[BRANCH_HEADER] = BRANCH_VALUE;
  }
  return h;
}

async function registerServiceWorkerSafe() {
  try {
    if (IS_CAPACITOR) return;
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    if (!isSecureForSW()) {
      console.warn("[SW] Not registering because site is not HTTPS.");
      return;
    }

    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "./" });

    try {
      await reg.update();
    } catch {}

    async function forceActivateWaitingSW() {
      try {
        if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
      } catch {}
    }

    await forceActivateWaitingSW();

    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed") {
          if (navigator.serviceWorker.controller) forceActivateWaitingSW();
        }
      });
    });

    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });

    try {
      await navigator.serviceWorker.ready;
    } catch {}

    navigator.serviceWorker.addEventListener("message", (event) => {
      const msg = event?.data || {};
      if (msg?.type === "NAVIGATE" && msg?.url) {
        try {
          if (window.location.pathname !== msg.url) {
            window.history.pushState({}, "", msg.url);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        } catch {}
      }
    });
  } catch (e) {
    console.warn("[SW] registration failed:", e);
  }
}

function AppBootstrap() {
  const mountedRef = useRef(false);

  const [apiBase, _setApiBase] = useState(readApiBase);
  const [themePref, _setThemePref] = useState(readThemePref);
  const [connection, setConnection] = useState(readConnection);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const setApiBase = (next) => {
    const v = String(next || "").trim();
    if (!v) return;
    try {
      localStorage.setItem(LS_API, v);
    } catch {}
    window.__API_BASE__ = v;
    _setApiBase(v);
  };

  const setThemePref = (pref) => {
    const v = String(pref || "system");
    try {
      localStorage.setItem(LS_THEME, v);
    } catch {}
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", v);
    }
    _setThemePref(v);
  };

  const runtime = useMemo(
    () => ({
      apiBase,
      connection,
      themePref,
      online,
      setApiBase,
      setThemePref,
    }),
    [apiBase, connection, themePref, online]
  );

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    registerServiceWorkerSafe();

    function onPerf(e) {
      console.debug("[app:perf]", e.detail || window.__APP_PERF__);
    }
    window.addEventListener("app:perf", onPerf);

    function onCmdPalette() {
      window.dispatchEvent(new CustomEvent("ui:open-command-palette"));
    }

    function onTogglePerfPanel() {
      window.dispatchEvent(new CustomEvent("ui:toggle-perf-panel"));
    }

    window.addEventListener("app:command-palette", onCmdPalette);
    window.addEventListener("app:toggle-perf-panel", onTogglePerfPanel);

    const mo = new MutationObserver(() => {
      const pref = readThemePref();
      _setThemePref(pref);
    });

    if (typeof document !== "undefined") {
      mo.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
    }

    function onStorage(e) {
      if (e.key === LS_API) {
        const v = readApiBase();
        _setApiBase(v);
        window.__API_BASE__ = v;
      }
      if (e.key === LS_THEME) {
        const v = readThemePref();
        _setThemePref(v);
      }
    }

    window.addEventListener("storage", onStorage);

    function onOnline() {
      setOnline(true);
    }

    function onOffline() {
      setOnline(false);
    }

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    const conn = navigator.connection;

    function onConnChange() {
      setConnection(readConnection());
    }

    if (conn && conn.addEventListener) {
      conn.addEventListener("change", onConnChange);
    }

    window.__setApiBase = window.__setApiBase || setApiBase;
    window.__buildAuthHeaders = window.__buildAuthHeaders || buildAuthHeaders;

    return () => {
      window.removeEventListener("app:perf", onPerf);
      window.removeEventListener("app:command-palette", onCmdPalette);
      window.removeEventListener("app:toggle-perf-panel", onTogglePerfPanel);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);

      if (conn && conn.removeEventListener) {
        conn.removeEventListener("change", onConnChange);
      }

      mo.disconnect();
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    window.__RUNTIME__ = runtime;
  }, [runtime]);

  return (
    <RuntimeContext.Provider value={runtime}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </RuntimeContext.Provider>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { err: error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info);
    this.setState({ err: error, info });
  }

  componentDidMount() {
    this._onWinErr = (e) => {
      const err = e?.error || e?.reason || e?.message || e;
      if (!this.state.err) this.setState({ err });
    };

    window.addEventListener("error", this._onWinErr);
    window.addEventListener("unhandledrejection", this._onWinErr);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this._onWinErr);
    window.removeEventListener("unhandledrejection", this._onWinErr);
  }

  render() {
    const { err, info } = this.state;

    if (err) {
      const stack = String(err?.stack || err);
      const componentStack = info?.componentStack ? String(info.componentStack) : "";

      return (
        <div style={{ padding: 16, fontFamily: "ui-sans-serif, system-ui" }}>
          <h2 style={{ marginTop: 0 }}>App failed to load</h2>

          <p style={{ opacity: 0.8 }}>
            Open Android Studio Logcat and filter by chromium or Capacitor.
          </p>

          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {stack}
            {componentStack ? "\n\n--- Component Stack ---\n" + componentStack : ""}
          </pre>

          <button
            onClick={() => location.reload()}
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.15)",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const rootEl = document.getElementById("root");

if (!rootEl) {
  throw new Error("Root element #root not found");
}

createRoot(rootEl).render(
  <ErrorBoundary>
    <AppBootstrap />
  </ErrorBoundary>
);